import React from "react"
import { ButtonType, Trustpilot } from "components/atoms"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { EventAction, EventCategory } from "models/GoogleAnalytics"

import MojoLogo from "url:assets/images/mojo-logo.png"
import HeroImage from "url:assets/images/illustrations/Key 2.png"
import PhoneIllustration from "url:assets/images/phone-rate-check-notification.svg"
import ConveyancingIllustration from "url:assets/images/offer-accepted-status/conveyancing.png"
import { useHistory } from "react-router-dom"
import {
  ContentContainer,
  AuthorName,
  CTAButton,
  Disclaimer,
  Footer,
  FooterLogo,
  FooterText,
  FreeConsultationText,
  HeroContent,
  HeroIllustration,
  HeroList,
  HeroSection,
  HeroText,
  HeroTitle,
  MainContent,
  ReviewAuthor,
  ReviewContent,
  ReviewDate,
  ReviewMeta,
  ReviewQuote,
  ReviewText,
  ReviewTitle,
  StarRating,
  TrustpilotContainer,
  TrustpilotSection,
  TrustpilotTitle,
} from "./shared/StyledComponents"
import { SectionCardComponent } from "./components/SectionCard"

interface Props {
  friendlyId: string
}

export const SpecialistStatusPage: React.FC<Props> = ({ friendlyId }) => {
  const history = useHistory()

  const handleBookAppointment = () => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: "Book Appointment - Specialist",
    })
    window.location.href = `${process.env.MOJO_RESULTS_URL}/${friendlyId}/nurture`
  }

  const handleLearnMore = (section: string, uri: string) => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: `Learn More - ${section}`,
    })
    history.push(uri)
  }

  return (
    <ContentContainer>
      <MainContent>
        <HeroSection>
          <HeroContent>
            <HeroTitle>Secure the right mortgage for your property</HeroTitle>
            <HeroText>
              With Mojo Specialist, your Mortgage Expert will help you navigate the market to
              provide you with a clear, personal recommendation.
            </HeroText>
            <HeroText>
              Should you apply, a <b>£499</b> non-refundable fee covers expert application and
              packaging from a dedicated Case Manager.
            </HeroText>
            <HeroText>
              You also get our Rate Check Promise (if eligible), which automatically scans for
              better rates from your chosen lender, which has provided our customers with an average
              saving of £748*.
            </HeroText>
            <CTAButton
              type={ButtonType.Solid}
              onClick={handleBookAppointment}
              text="Book your Buy-to-Let Specialist Advice"
            />
          </HeroContent>
          <HeroIllustration>
            <img src={HeroImage} alt="Property illustration" />
          </HeroIllustration>
        </HeroSection>
        <SectionCardComponent
          title="Our Rate Check Promise"
          paragraphs={[
            "Secure your mortgage with the confidence that you're on the best possible rate. The mortgage market moves fast, and rates can change frequently.",
            "Our Rate Check Promise is a high-level service designed to ensure you don't miss out on a better deal that you've applied with us or a lender.",
          ]}
          buttonText="Learn more"
          buttonType={ButtonType.Solid}
          onButtonClick={() =>
            handleLearnMore("Rate Check", `/application/${friendlyId}/rate-check-promise`)
          }
          imageSrc={PhoneIllustration}
          imageAlt="Rate check illustration"
          pillText="Included service"
        />

        <SectionCardComponent
          title="Secure your property's legal work"
          paragraphs={[
            "Conveyancing is a vital part of securing your property. It's important to instruct your chosen solicitor as soon as you're ready.",
            "Your Mortgage Expert has provided a number of quotes for you to compare and review.",
          ]}
          buttonText="View conveyancing quotes"
          buttonType={ButtonType.Solid}
          onButtonClick={() =>
            handleLearnMore("Conveyancing", `/application/${friendlyId}/conveyancing`)
          }
          imageSrc={ConveyancingIllustration}
          imageAlt="Legal conveyancing illustration"
        />

        <TrustpilotSection>
          <ReviewContent>
            <TrustpilotTitle>What our customers say</TrustpilotTitle>
            <ReviewQuote>
              <ReviewTitle>After so many struggles with my actual…</ReviewTitle>
              <ReviewText>
                After so many struggles with my actual mortgage lender, a well known bank, it was a
                pleasure to talk with the advisor from Mojo. They explained everything really
                clearly and offered me advice on which lender to go with. Would absolutely
                recommend.
              </ReviewText>
            </ReviewQuote>
            <ReviewMeta>
              <ReviewAuthor>
                <StarRating>★★★★★</StarRating>
                <AuthorName>Jenna</AuthorName>
              </ReviewAuthor>
              <ReviewDate>17 July 2024</ReviewDate>
            </ReviewMeta>
          </ReviewContent>
          <TrustpilotContainer>
            <Trustpilot
              height="240px"
              width="100%"
              templateId="54d0e1d8764ea9078c79e6ee"
              theme="light"
            />
          </TrustpilotContainer>
        </TrustpilotSection>
        <Disclaimer>
          *Data taken using Mojo’s internal records, covering the period from 18 February 2025 to 30
          September 2025. Average savings over the initial term based on the difference between the
          customer’s initial pay rate and their new, lower rate discovered through our Rate Check
          Promise service.*
        </Disclaimer>
        <Footer>
          <FooterLogo src={MojoLogo} alt="Mojo" />
          <FooterText>
            Mojo is a trading style of Life's Great Limited which is registered in England and Wales
            (06246376). We are authorised and regulated by the Financial Conduct Authority and are
            on the Financial Services Register (478215) Our registered office is The Cooperage, 5
            Copper Row, London, SE1 2LH, and head office is Mojo Mortgages, WeWork No. 1
            Spinningfields, Quay Street, Manchester, M3 3JE.
          </FooterText>
        </Footer>
      </MainContent>
    </ContentContainer>
  )
}
